import React, { useState, useRef, useEffect } from 'react';
import { Col } from 'react-bootstrap';
import { graphql } from '@apollo/client/react/hoc';
import { withRouter } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import SelectSubscription from '../select-subscription-v2';
import Loading from '../loading';
import compose from '../../services/apolloCompose';
import Environment from '../../services/Environment';
import BBAnalytics from '../../services/BBAnalytics';
import GraphQLTags from '../../services/GraphQLTags';
import PreCheckoutPopup from '../preCheckout-popup';
import BitsyModal from '../bitsy-modal';
import './style.css';

const CHARACTERS_REQUIRING_MARGIN = 4;

const renderError = (isTopLevelComponent, history) => {
  if (isTopLevelComponent) {
    history.push('/error');
    return <Loading />;
  }
  return null;
};

const renderNoPlans = () => {
  return null;
};

const renderPartnerPlans = () => {
  return (
    <Col className='sub-wrapper linkToBumpCol'>
      <div>
        Please visit{' '}
        <a
          href='https://bumpboxes.com/'
          target='_blank'
          className='linkToBump'
          rel='noopener noreferrer'
          aria-label='Bump Boxes'
        >
          Bump Boxes
        </a>{' '}
        to subscribe!
      </div>
    </Col>
  );
};

// Lead with the 6 → 9 → 12 terms; any other plan (Month-to-Month, 3-month)
// keeps its existing order after them.
const TERM_ORDER = { 'Six Month': 1, 'Nine Month': 2, 'Twelve Month': 3 };

const getFilteredPlans = (plansForProvider, showMonthlyPlans, planFilter) => {
  let filteredPlans;
  if (showMonthlyPlans) {
    filteredPlans = plansForProvider.filter(
      (plan) => plan.months === 'INFINITY' && plan.type === 'BOX',
    );
  } else {
    filteredPlans = plansForProvider.filter(
      (plan) => plan.months !== 'INFINITY' && plan.type === 'BOX',
    );
  }

  filteredPlans = [...filteredPlans].sort(
    (a, b) => (TERM_ORDER[a.title] || 99) - (TERM_ORDER[b.title] || 99),
  );

  if (planFilter) {
    const splitPlans = planFilter.split(',');

    const newPlans = [];

    splitPlans.forEach((splitPlan) => {
      const monthsNumber = splitPlan.replace('b', '');
      const isBundled = splitPlan.endsWith('b');

      let foundMatchingPlan;
      if (isBundled) {
        foundMatchingPlan = filteredPlans.find(
          (plan) => plan.months === monthsNumber,
        );
      } else if (monthsNumber === 'm2m') {
        foundMatchingPlan = filteredPlans.find(
          (plan) => plan.title === 'Month to Month',
        );
      } else {
        foundMatchingPlan = filteredPlans.find(
          (plan) => plan.minimumMonths === Number(monthsNumber),
        );
      }

      if (foundMatchingPlan) {
        newPlans.push(foundMatchingPlan);
      }
    });

    if (newPlans.length > 0) {
      filteredPlans = newPlans;
    }
  }

  return filteredPlans;
};

const getMaxBulletLength = (plansForProvider) => {
  // Determine if any of the boxes require more or less spacing because
  // the content is longer.
  const point1Lengths = plansForProvider.map((plan) => {
    let point1;
    if (plan.highlights && plan.highlights.length) {
      point1 = plan.highlights.find((highlight) => highlight.order === 1);
    } else {
      point1 = plan.bulletPoints.point1;
    }
    return point1.length;
  });
  return Math.max(...point1Lengths);
};

const sendLoadEvent = () => {
  // used to set an activation event for Google Optimize
  const event = 'optimize.activate.plansvisible';
  BBAnalytics.addActivationEvent(event);
};

const subscriptionCouponsFromURLWithLocalStorageHandler = (props) => {
  let subscriptionCouponsFromURL =
    props.getSubscriptionCouponsByCode?.getSubscriptionCouponsByCode;
  if (!subscriptionCouponsFromURL) {
    const couponsFromStorage = localStorage.getItem(
      'subscriptionCouponsFromURL',
    );
    const subscriptionCouponsFromURLSetAt = localStorage.getItem(
      'subscriptionCouponsFromURLSetAt',
    );
    if (couponsFromStorage && subscriptionCouponsFromURLSetAt) {
      const tenDays = 10 * 24 * 60 * 60 * 1000;
      const tenDaysAgo = new Date(Date.now() - tenDays);
      if (tenDaysAgo < new Date(Number(subscriptionCouponsFromURLSetAt))) {
        subscriptionCouponsFromURL = JSON.parse(couponsFromStorage);
      } else {
        localStorage.removeItem('subscriptionCouponsFromURL');
        localStorage.removeItem('subscriptionCouponsFromURLSetAt');
      }
    }
  } else if (subscriptionCouponsFromURL && subscriptionCouponsFromURL.length) {
    localStorage.setItem(
      'subscriptionCouponsFromURL',
      JSON.stringify(subscriptionCouponsFromURL),
    );
    localStorage.setItem('subscriptionCouponsFromURLSetAt', Date.now());
  }

  return subscriptionCouponsFromURL;
};

const renderModal = (props) => {
  if (props.modalType === 'PRE-CHECKOUT PROMO') {
    return (
      <BitsyModal
        isShowingModal
        id='preCheckoutPromoModal'
        onExit={props.goToCart}
        type='modalWithComponent'
        content='preCheckoutPromoPopUpPremiumUpgrade'
        modalTitle={props.modalTitle}
        modalComponent={
          <PreCheckoutPopup
            updateTitle={props.updateTitle}
            handleAddPreCheckoutPromo={props.handleAddPreCheckoutPromo}
            exitModal={props.goToCart}
            isRecurringAddOnPromo
            id='subPreCheckoutPromo'
          />
        }
      />
    );
  }

  return null;
};

// Pricing cards in a swipe carousel on mobile (scroll-snap, peeking neighbours,
// prev/next arrows — SubPdp stages style) and a centered static row on desktop.
const renderPlans = (
  props,
  filteredPlans,
  maxBullet1,
  subscriptionCouponsFromURL,
  featuredPlanId,
  carousel,
) => {
  const { scrollerRef, atStart, atEnd, scrollBy } = carousel;
  return (
    <div className='sub-wrapper-outer'>
      <button
        type='button'
        className={`sub-wrapper-nav prev${atStart ? ' is-hidden' : ''}`}
        aria-label='Previous plans'
        aria-hidden={atStart}
        tabIndex={atStart ? -1 : 0}
        onClick={() => scrollBy(-1)}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
      </button>
      <div
        className='sub-wrapper'
        id={props.subBoxContainerId ? props.subBoxContainerId : 'sub-wrapper'}
        ref={scrollerRef}
      >
        {renderModal(props)}
        {filteredPlans.map((plan) => (
          <SelectSubscription
            referralCode={props.referralCode}
            key={plan._id}
            plan={plan}
            nextStep={props.nextStep}
            requiresExtraMargin={
              plan.bulletPoints.point1.length <
              maxBullet1 - CHARACTERS_REQUIRING_MARGIN
            }
            isTopLevelComponent={props.isTopLevelComponent}
            handleAddPreCheckoutPromo={props.handleAddPreCheckoutPromo}
            modalType={props.modalType}
            goToCart={props.goToCart}
            subscriptionCouponsFromURL={subscriptionCouponsFromURL}
            featured={plan._id === featuredPlanId}
          />
        ))}
      </div>
      <button
        type='button'
        className={`sub-wrapper-nav next${atEnd ? ' is-hidden' : ''}`}
        aria-label='Next plans'
        aria-hidden={atEnd}
        tabIndex={atEnd ? -1 : 0}
        onClick={() => scrollBy(1)}
      >
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  );
};

// The recommended plan — 6 Month for monthly, 9 Month for prepaid. Rendered as
// the filled/featured card.
const getFeaturedPlanId = (filteredPlans, showMonthlyPlans) => {
  const featuredTitle = showMonthlyPlans ? 'Six Month' : 'Nine Month';
  const featured = filteredPlans.find((plan) => plan.title === featuredTitle);
  return (featured || filteredPlans[0])?._id;
};

const SelectSubscriptionBoxesWithoutData = (props) => {
  // Mobile swipe-carousel: arrow scrolling + edge detection to hide arrows at
  // the ends (mirrors SubPdp's stages carousel). No-ops on the desktop row.
  const scrollerRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector('.subContainer');
    const amount = card ? card.offsetWidth + 16 : el.clientWidth * 0.85;
    el.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const update = () => {
      // 1px slack absorbs sub-pixel rounding so the end arrows reliably hide.
      setAtStart(el.scrollLeft <= 1);
      setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
    // Re-measure when the plan group changes (Monthly⇄Prepaid).
  }, [props.showMonthlyPlans, props.planFilter]);

  const { plansForProvider, error } = props.plansForProviderQuery;
  if (error) {
    return renderError(props.isTopLevelComponent, props.history);
  }

  const subscriptionCouponsFromURL = subscriptionCouponsFromURLWithLocalStorageHandler(
    props,
  );

  if (!plansForProvider || plansForProvider.length === 0) {
    if (Environment.store === 'pnmbump' || Environment.store === 'wtebump') {
      return renderPartnerPlans();
    }
    return renderNoPlans();
  }

  const filteredPlans = getFilteredPlans(
    plansForProvider,
    props.showMonthlyPlans,
    props.planFilter,
  );
  const maxBullet1 = getMaxBulletLength(filteredPlans);

  sendLoadEvent();

  const featuredPlanId = getFeaturedPlanId(
    filteredPlans,
    props.showMonthlyPlans,
  );

  return renderPlans(
    props,
    filteredPlans,
    maxBullet1,
    subscriptionCouponsFromURL,
    featuredPlanId,
    { scrollerRef, atStart, atEnd, scrollBy },
  );
};

const SelectSubscriptionBoxesWithData = compose(
  graphql(GraphQLTags.queries.plansForProviderQuery, {
    name: 'plansForProviderQuery',
    options: () => ({
      variables: {
        provider: Environment.store,
      },
      notifyOnNetworkStatusChange: true,
    }),
  }),
  graphql(GraphQLTags.queries.getSubscriptionCouponsByCode, {
    name: 'getSubscriptionCouponsByCode',
    skip: (ownProps) => !ownProps.matchParams?.couponCode,
    options: (ownProps) => ({
      variables: {
        code: ownProps.matchParams?.couponCode || undefined,
      },
    }),
  }),
)(withRouter(SelectSubscriptionBoxesWithoutData));

export { SelectSubscriptionBoxesWithoutData };

export default SelectSubscriptionBoxesWithData;
